// QuizOverview.js

import React, { useEffect, useState } from "react";
import styled from "styled-components";
import HistoryOverview from "./component/HistoryOverview/HistoryOverview";
import PdfWrapper from "./results/Pdf/PdfWrapper";
import Webpage from "./results/Webpage/Webpage";
import { darken, QuizScreen, tint } from "./types/GeneralStyles";
import QuestionPublicLink from "./component/PublicLink/PublicLink";
import QuestionOpen from "./types/Question/Open/QuestionOpen";
import QuestionSorteer from "./types/Question/Sorteer/QuestionSorteer";
import QuestionStelling from "./types/Question/Stelling/QuestionStelling";
import ResultTitle from "./types/Title/ResultTitle";
import ShapeProgress from "./component/QuizShape/Shape/ShapeProgress";
import StartScreen from "./types/StartScreen/StartScreen";
import QuizContent from "./types/Content/QuizContent";
import SimpleResult from "./results/SimpleResult/SimpleResult";
import { clearDB, saveToDB } from "./component/indexedDB/indexedDB";
import QuizShape from "./component/QuizShape/Shape/QuizShape";
import QuizGrid from "./component/QuizShape/Grid/QuizGrid";
import CommunicatingIframe from "./types/iframe/QuizCommunicatingIframe";
import CommunicatingResult from "./results/CommunicatingResult/CommunicatingResult";
import SendToAPI from "./results/SendToAPI/SendToAPI";
import QuizButtonAction from "./results/QuizButtonAction/QuizButtonAction";
import QuestionUpload from "./types/Question/Upload/QuestionUpload";

export const NavigationButtons = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;
export const NavigationButton = styled.button`
  ${(props) => props.bgColor && `background-color: ${props.bgColor};`}
  padding: 20px 30px;
  font-size: 1.3em;
  border: none;
  font-weight: bold;
  border-radius: 10px;
  box-shadow: 0 4px 6px -1px #001d2910, 0 2px 4px -2px #001d2910;
  cursor: pointer;
  color: white;
  margin: 20px 0;
  visibility: ${(props) => (props.canBeClicked ? "hidden" : "visible")};
  &:hover {
    background: ${(props) => darken(props.bgColor, 25)};
    color: white;
  }
  &:disabled {
    background: ${(props) => tint(props.bgColor, 80)};
    color: white;
    cursor: not-allowed;
  }
  @media only screen and (max-width: 600px) {
    padding: 20px 15px;
  }
`;

const QuizOverview = ({ quiz, oriQuiz, loading, error }) => {
  const [navigationEnabled, setNavigationEnabled] = useState(false);
  const [quizProgress, setQuizProgress] = useState(
    quiz.quiz_shape.map(() => ({ currentIndex: 0 })) // Initialize progress for each quizShape
  );
  const [dbName, setDbName] = useState("");
  useEffect(() => {
    setDbName(sessionStorage.getItem("currentSessionName") || "Leerling");
  }, []);

  const resetQuiz = () => {
    setQuizProgress(quiz.quiz_shape.map(() => ({ currentIndex: 0 })));
    setNavigationEnabled(false);
    clearDB(dbName); // Ensure dbName is defined and accessible
  };

  const handleEnableNavigation = () => {
    setNavigationEnabled(true);
  };

  if (loading) {
    return <div>Laden ...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  // Helper functions to extract URL parameters
  const getQuestionParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("question");
  };

  const getQuestionAllParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.getAll("question");
  };

  const getResultParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("result");
  };

  const getResultAllParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.getAll("result");
  };

  const getShapeParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get("shape");
  };

  const getShapeAllParam = () => {
    const params = new URLSearchParams(window.location.search);
    return params.getAll("shape");
  };

  const questionParam = getQuestionParam();
  const questionAllParam = getQuestionAllParam();
  const resultParam = getResultParam();
  const resultAllParam = getResultAllParam();
  const shapeParam = getShapeParam();
  const shapeAllParam = getShapeAllParam();

  const isStartScreen = !questionParam && !resultParam && !shapeParam;
  const isStartScreenOrContainsStartScreenName =
    isStartScreen || questionAllParam.includes("startscreen_name");

  const quizItems = quiz.Quizes;
  const quizShapes = quiz.quiz_shape;
  const quizResults = quiz.quiz_results;

  // Helper functions to find quiz items and results by ID
  function findQuizItemById(quizItemId) {
    return quizItems.find((quiz) => quiz.quiz_item_id === quizItemId);
  }

  function findQuizResultById(resultItemId) {
    return quizResults.find(
      (result) => result.results_item_id === resultItemId
    );
  }

  // Navigation Handlers
  const handlePrevious = (index) => {
    setQuizProgress((prevState) =>
      prevState.map((shape, i) =>
        i === index
          ? {
              ...shape,
              currentIndex: shape.currentIndex > 0 ? shape.currentIndex - 1 : 0,
            }
          : shape
      )
    );
  };

  const handleNext = (index, shapeLength) => {
    setNavigationEnabled(false); // Disable after clicking
    setQuizProgress((prevState) =>
      prevState.map((shape, i) =>
        i === index
          ? {
              ...shape,
              currentIndex:
                shape.currentIndex < shapeLength - 1
                  ? shape.currentIndex + 1
                  : shape.currentIndex,
            }
          : shape
      )
    );
  };

  // Function to render components based on their type
  const renderComponent = (item, index, quiz, enableNavigationButton) => {
    if (!item) {
      console.error("Item is undefined:", item);
      return <p key={index}>Fout: Componentgegevens ontbreken.</p>;
    }

    let itemObj = item;

    // If the item doesn't have a __component, attempt to extract it from item.data
    if (!item.__component) {
      if (item.data && item.data.__component) {
        itemObj = item.data;
      } else {
        console.error("Item inside data with __component is undefined:", item);
        return <p key={index}>Fout: Componentgegevens ontbreken.</p>;
      }
    }

    // Handle special cases if any (e.g., 'startscreen_name' as StartScreen)
    if (itemObj.__component === "quiz-game-types.start-screen") {
      return (
        <StartScreen
          key={index}
          content={quiz}
          overViewPage={isStartScreen}
          // Pass any additional props if needed
        />
      );
    }

    // Render the appropriate component based on __component type
    switch (itemObj.__component) {
      case "quiz-game-types.sorteer":
        return (
          <QuestionSorteer
            key={index}
            question={itemObj}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
          />
        );
      case "quiz-game-types.stelling":
        return (
          <QuestionStelling
            key={index}
            question={itemObj}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
          />
        );
      case "quiz-game-types.open-vragen":
        return (
          <QuestionOpen
            key={index}
            question={itemObj}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
          />
        );
      case "quiz-game-types.photo-upload":
        return (
          <QuestionUpload
            key={index}
            question={itemObj}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
            // onComplete / onCancel zijn optioneel hier
          />
        );
      case "quiz-game-types.result-title":
        return (
          <ResultTitle
            key={index}
            question={itemObj}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
          />
        );
      case "quiz-game-types.content":
        return (
          <QuizContent
            key={index}
            question={itemObj}
            overViewPage={isStartScreen}
          />
        );
      case "quiz-game-types.comunictating-iframe":
        return (
          <CommunicatingIframe
            key={index}
            id={itemObj.quiz_item_id}
            url={itemObj.link_to_embed} // Ensure 'link_to_embed' contains the URL
            onSuccess={() => {
              enableNavigationButton();
              // Save success to IndexedDB
              const attempt = {
                id: itemObj.quiz_item_id,
                answer: true, // Indicates success
                attempts: 1, // Initial attempt
              };
              saveToDB(
                [attempt],
                sessionStorage.getItem("currentSessionName") || "Leerling"
              );
            }}
            onFailure={() => {
              enableNavigationButton();
              // Optionally handle failure
              const attempt = {
                id: itemObj.quiz_item_id,
                answer: false, // Indicates failure
                attempts: 1,
              };
              saveToDB(
                [attempt],
                sessionStorage.getItem("currentSessionName") || "Leerling"
              );
            }}
            theme_color={quiz.theme_color}
            overViewPage={isStartScreen}
          />
        );
      case "quiz-game-types.combine-formats":
        // Correct implementation for combine-formats
        return (
          <React.Fragment key={index}>
            {itemObj.stack.map((stackItem, stackIndex) => (
              <React.Fragment key={`${index}-${stackIndex}`}>
                {renderComponent(
                  stackItem.data,
                  `${index}-${stackIndex}`,
                  quiz,
                  handleEnableNavigation
                )}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      case "quiz-game-types.simple-result":
        return (
          <SimpleResult
            key={index}
            result={itemObj}
            questions={oriQuiz.Quizes}
            oriQuiz={oriQuiz}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
            resetQuiz={resetQuiz} // Pass resetQuiz function as prop
          />
        );
      case "quiz-game-types.button-action":
        return (
          <QuizButtonAction
            key={index}
            result={itemObj}
            questions={oriQuiz.Quizes}
            oriQuiz={oriQuiz}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
            resetQuiz={resetQuiz} // Pass resetQuiz function as prop
          />
        );

      case "quiz-game-types.communicating-result":
        return (
          <CommunicatingResult
            key={index}
            result={itemObj}
            overViewPage={isStartScreen}
          />
        );
      case "quiz-game-types.send-to-api":
        return (
          <SendToAPI
            key={index}
            result={itemObj}
            questions={oriQuiz.Quizes}
            oriQuiz={oriQuiz}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
            resetQuiz={resetQuiz} // Pass resetQuiz function as prop
          />
        );

      case "quiz-game-types.webpage":
        return (
          <Webpage
            key={index}
            result={itemObj}
            questions={oriQuiz.Quizes}
            oriQuiz={oriQuiz}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
            resetQuiz={resetQuiz} // Pass resetQuiz function as prop
          />
        );
      case "quiz-game-types.quiz-result-build":
        return (
          <PdfWrapper
            key={index}
            result={itemObj}
            questions={oriQuiz.Quizes}
            oriQuiz={oriQuiz}
            overViewPage={isStartScreen}
            enableNavigationButton={handleEnableNavigation}
            resetQuiz={resetQuiz} // Pass resetQuiz function as prop
          />
        );
      default:
        console.error("Unknown component type:", itemObj.__component);
        return <p key={index}>Fout: Onbekend componenttype.</p>;
    }
  };

  if (quiz.visible_for_teacher) {
    return (
      <div className={!questionParam ? "generalPage" : "main"}>
        <div
          id="loadingscreen"
          style={{
            display: loading ? "block" : "none",
            paddingTop: "20px",
            position: "absolute",
          }}
        >
          Loading...
        </div>
        <div id="overview">
          {/* StartScreen is now handled within the quiz_shape_order_combined */}
          {isStartScreen && (
            <StartScreen content={quiz} overViewPage={!questionParam} />
          )}
          {isStartScreen && <HistoryOverview />}

          {/* Render Quiz Items excluding combine-formats */}
          {(questionAllParam.length > 0 || isStartScreen) && (
            <>
              {quizItems
                .filter(
                  (item) =>
                    item.__component !== "quiz-game-types.combine-formats"
                )
                .map((item, index) =>
                  renderComponent(item, index, quiz, handleEnableNavigation)
                )}
            </>
          )}

          {/* Render Shapes */}
          {(shapeAllParam.length > 0 || isStartScreen) &&
            quizShapes.map(
              (shape, index) => {
                if (shape.__component === "quiz-shape.quiz-shape") {
                  return (
                    <QuizShape
                      key={shape.id}
                      shape={shape}
                      index={index}
                      quiz={quiz}
                      quizProgress={quizProgress}
                      overViewPage={isStartScreen}
                      setQuizProgress={setQuizProgress}
                      handleEnableNavigation={handleEnableNavigation}
                      enableNavigationButton={handleEnableNavigation}
                      renderComponent={renderComponent} // Pass the function
                    />
                  );
                } else if (shape.__component === "quiz-shape.quiz-grid") {
                  // Placeholder for future implementation
                  return (
                    <div key={shape.id}>
                      {/* You can create a separate QuizGrid component later */}
                      <QuizGrid
                        key={shape.id}
                        shape={shape}
                        index={index}
                        overViewPage={isStartScreen}
                        quiz={quiz}
                        renderComponent={renderComponent}
                      />
                      {/* <p>Quiz Grid Component Placeholder</p> */}
                    </div>
                  );
                } else {
                  return (
                    <div key={shape.id}>
                      <p>Unknown shape component type: {shape.__component}</p>
                    </div>
                  );
                }
              }
              // quizShapes.map((shape, index) => {
              //   const shapeLength = shape.quiz_shape_order_combined.length;
              //   const currentShapeProgress =
              //     quizProgress[index]?.currentIndex || 0;
              //   const progress =
              //     shapeLength > 1
              //       ? (currentShapeProgress / (shapeLength - 1)) * 100
              //       : 100;

              //   return (
              //     <QuizScreen
              //       bgColor={shape.theme_color}
              //       bgVisible={!shapeParam}
              //       id={shape.quiz_shape_id}
              //       key={index}
              //     >
              //       <QuestionPublicLink
              //         overViewPage={!shapeParam}
              //         type="shape"
              //         quiz_item_id={shape.quiz_shape_id}
              //       />
              //       <ShapeProgress
              //         bgColor={shape.theme_color}
              //         progress={progress}
              //       />

              //       <div className="item-container">
              //         {renderComponent(
              //           shape.quiz_shape_order_combined[currentShapeProgress]
              //             ?.data, // Ensure to pass the data object
              //           currentShapeProgress,
              //           quiz,
              //           handleEnableNavigation
              //         )}
              //       </div>

              //       <NavigationButtons className="navigation-buttons">
              //         <NavigationButton
              //           onClick={() => handlePrevious(index)}
              //           bgColor={shape.theme_color}
              //           canBeClicked={currentShapeProgress === 0}
              //           disabled={!navigationEnabled}
              //         >
              //           {shape.quiz_shape_order_combined[currentShapeProgress]
              //             ?.custom_prev_text ?? shape.prev_text}
              //         </NavigationButton>
              //         <NavigationButton
              //           onClick={() => handleNext(index, shapeLength)}
              //           bgColor={shape.theme_color}
              //           canBeClicked={currentShapeProgress === shapeLength - 1}
              //           disabled={!navigationEnabled}
              //         >
              //           {shape.quiz_shape_order_combined[currentShapeProgress]
              //             ?.custom_next_text ?? shape.next_text}
              //         </NavigationButton>
              //       </NavigationButtons>
              //     </QuizScreen>
              //   );
              // }
            )}

          {/* Render Results */}
          {(resultAllParam.length > 0 || isStartScreen) && (
            <>
              {quizResults.map((item, index) =>
                renderComponent(item, index, quiz, handleEnableNavigation)
              )}
            </>
          )}
        </div>
      </div>
    );
  } else {
    return <div>Helaas, deze Quiz is niet zichtbaar</div>;
  }
};

export default QuizOverview;
